import { useEffect, useMemo, useState } from 'react';

import {
	BookAltOutline,
	ChartBarOutline,
	ChatAltOutline,
	DocumentReportOutline,
	GiftOutline,
	LifeRaftOutline,
	OfficeBuildingOutline,
	PlantOutline,
	PuzzleOutline,
	UserGroupOutline,
	ViewGridOutline,
} from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { Permission, useCurrentCompany, useCurrentUser, useFeatureFlag, usePermission } from '@bonusly/who-am-i';

import { AppPath, Route } from '../../../config/router';
import { Variant } from '../../../lib/experiment';
import { adminRewardsPath } from '../../../utils/api';
import { NavConfig, NavLinkConfig } from '../types/nav';

// FIXME: We shouldn't be doing all of these dynamic splices/pushes/shifts etc to arrays based on flags. Let's just
// update the config object to allow for required flags/permissions to be enabled.

export const useNavOptions = () => {
	const user = useCurrentUser();
	const hasAchievePackage = usePermission(Permission.CanAccessAchieve);
	const hasAchieveLimitedRelease = useFeatureFlag(Variant.AchieveLimitedRelease);
	const isAchieveEnabled = useFeatureFlag(Variant.AchieveEarlyAccess) || hasAchievePackage || hasAchieveLimitedRelease;
	const canGiveRecognition = usePermission(Permission.CanGiveRecognition);
	const company = useCurrentCompany();
	const r = useBuildRecognizeURL();
	const isIncentivesRedesignEnabled = useFeatureFlag('incentives-redesign');
	const pendingApprovalsCount = user.pending_awards_count || 0;
	const reportedPostsCount = user.unresolved_bonus_reports_count || 0;
	const recognitionBadgeCount = pendingApprovalsCount + reportedPostsCount;

	// Use state to track pathname so component re-renders on URL change
	const [currentPathname, setCurrentPathname] = useState(window.location.pathname);

	// Listen for URL changes
	useEffect(() => {
		const handleLocationChange = () => {
			setCurrentPathname(window.location.pathname);
		};

		// Check immediately on mount
		handleLocationChange();

		// Listen for popstate events (browser back/forward)
		window.addEventListener('popstate', handleLocationChange);

		// Also listen for pushstate/replacestate (programmatic navigation)
		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		window.history.pushState = function (...args) {
			originalPushState.apply(window.history, args);
			handleLocationChange();
		};

		window.history.replaceState = function (...args) {
			originalReplaceState.apply(window.history, args);
			handleLocationChange();
		};

		// Fallback: check periodically in case events are missed
		const intervalId = setInterval(handleLocationChange, 1000);

		return () => {
			window.removeEventListener('popstate', handleLocationChange);
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
			clearInterval(intervalId);
		};
	}, []);

	return useMemo(() => {
		const companyLinks: NavLinkConfig[] = [
			{
				roles: ['global_admin'],
				label: 'admin_menu.company.account_settings',
				url: '/admin/company/settings',
			},
			{
				roles: ['global_admin', 'tech_admin'],
				label: 'admin_menu.company.integrations',
				url: r(Route.Integrations),
				isRecognizeRoute: true,
			},
			{
				roles: ['global_admin', 'finance_admin'],
				label: 'admin_menu.company.billing_settings',
				url: r(Route.AdminBilling),
				isRecognizeRoute: true,
			},
			{
				roles: ['global_admin', 'finance_admin'],
				label: 'admin_menu.company.billing_history',
				url: r(Route.AdminBillingActivity),
				isRecognizeRoute: true,
			},
			{
				roles: ['global_admin', 'rewards_admin'],
				label: 'admin_menu.company.rewards_catalog',
				url: adminRewardsPath(AppPath.Catalog),
			},
			{
				roles: ['global_admin'],
				label: 'admin_menu.company.programs_management',
				url: '/admin/programs/settings',
			},
			{
				roles: ['global_admin'],
				label: 'admin_menu.company.announcements',
				url: '/admin/company/announcements',
			},
		];

		const userLinks: NavLinkConfig[] = [
			{
				roles: ['global_admin'],
				label: 'admin_menu.users.user_accounts',
				url: r(Route.AdminUsers),
				isRecognizeRoute: true,
			},
			{
				roles: ['global_admin'],
				label: 'admin_menu.users.user_managment',
				url: r(company.user_management_integration_url || Route.AdminEmptyIntegration),
				isRecognizeRoute: true,
			},
		];

		if (!company.user_management_integration_url) {
			userLinks.push({
				roles: ['global_admin'],
				label: 'admin_menu.users.manage_user_import',
				url: r(Route.AdminImportedUsers),
				isRecognizeRoute: true,
			});
		}

		const recognitionLinks: NavLinkConfig[] = [
			{
				roles: ['global_admin', 'awards_admin'],
				label: isIncentivesRedesignEnabled ? 'admin_menu.recognition.awards_management' : 'admin_menu.recognition.awards_management_old',
				url: r(Route.AdminAwards),
				isRecognizeRoute: true,
			},
			{
				roles: ['global_admin', 'awards_admin'],
				label: 'admin_menu.recognition.nominations',
				matchActive: /\/admin\/programs\/nominations/,
				url: '/admin/programs/nominations',
			},
			{
				roles: ['global_admin', 'awards_admin'],
				label: 'admin_menu.recognition.celebrations',
				matchActive: /\/admin\/programs\/celebrations/,
				url: '/admin/programs/celebrations',
			},
			{
				roles: ['global_admin', 'awards_admin'],
				features: ['incentives-redesign'],
				label: 'admin_menu.recognition.incentives',
				matchActive: /\/admin\/programs\/incentives/,
				url: '/admin/programs/incentives',
			},
			{
				badgeCount: pendingApprovalsCount,
				roles: ['global_admin', 'awards_admin', 'award_approver'],
				label: 'admin_menu.recognition.pending',
				url: r(Route.AdminAwardsPending),
				isRecognizeRoute: true,
			},
			{
				badgeCount: reportedPostsCount,
				roles: ['global_admin'],
				label: 'admin_menu.recognition.reported_posts',
				url: r(Route.AdminBonusReports),
				isRecognizeRoute: true,
			},
		];

		const oneOnOnesLinks: NavLinkConfig[] = [
			{
				roles: ['global_admin', 'awards_admin'],
				label: 'admin_menu.oneonones.overview',
				matchActive: /one-on-ones\/(?!logs)/,
				url: '/admin/growth/one-on-ones',
			},
		];

		if (user.people_data_admin) {
			oneOnOnesLinks.push({
				roles: ['global_admin', 'awards_admin'],
				label: 'admin_menu.oneonones.logs',
				matchActive: /one-on-ones\/logs/,
				url: '/admin/growth/one-on-ones/logs',
			});
		}

		const reportLinks: NavLinkConfig[] = [
			{
				roles: ['global_admin', 'awards_admin', 'finance_admin', 'rewards_admin'],
				label: 'admin_menu.reports.subscriptions',
				url: '/admin/subscriptions',
			},
			{
				roles: ['global_admin', 'awards_admin'],
				label: 'admin_menu.reports.recognition_activity',
				url: r(Route.AdminRecognition),
				isRecognizeRoute: true,
			},
			{
				roles: ['global_admin', 'awards_admin'],
				label: 'admin_menu.reports.incentives_summary',
				url: '/admin/incentives/summary',
			},
			{
				roles: ['global_admin', 'finance_admin'],
				label: 'admin_menu.reports.points_earned',
				url: r(Route.AdminEarnings),
				isRecognizeRoute: true,
			},
			{
				roles: ['global_admin', 'rewards_admin'],
				label: 'admin_menu.reports.redemptions_activity',
				url: r(Route.AdminRewards),
				isRecognizeRoute: true,
			},
			{
				roles: ['global_admin', 'finance_admin'],
				label: 'admin_menu.reports.rewards_cost',
				url: r(Route.AdminTotals),
				isRecognizeRoute: true,
			},
		];

		if (canGiveRecognition) {
			recognitionLinks.unshift({
				roles: ['global_admin'],
				label: 'admin_menu.recognition.peer_to_peer',
				url: '/admin/programs/peer-to-peer',
			});

			reportLinks.splice(2, 0, {
				roles: ['global_admin'],
				label: 'admin_menu.reports.participation_summary',
				url: r(Route.AdminParticipation),
				isRecognizeRoute: true,
			});

			reportLinks.splice(5, 0, {
				roles: ['global_admin', 'finance_admin'],
				label: 'admin_menu.reports.points_given',
				url: r(Route.AdminGiving),
				isRecognizeRoute: true,
			});
		}

		const admin: NavConfig = {
			id: 'admin-nav',
			label: 'admin_menu.menu_label',
			title: 'admin_menu.title',
			links: [
				{
					icon: ViewGridOutline,
					roles: ['global_admin', 'finance_admin'],
					label: 'admin_menu.dashboard',
					url: '/admin/dashboard',
				},
			],
			sections: [
				{
					icon: OfficeBuildingOutline,
					id: 'company-nav',
					title: 'admin_menu.company.title',
					links: companyLinks,
				},
				{
					icon: UserGroupOutline,
					id: 'users-nav',
					title: 'admin_menu.users.title',
					links: userLinks,
				},
				{
					badgeCount: recognitionBadgeCount,
					icon: ChatAltOutline,
					id: 'recognition-nav',
					title: 'admin_menu.recognition.title',
					links: recognitionLinks,
				},
				{
					icon: DocumentReportOutline,
					id: 'reports-nav',
					title: 'admin_menu.reports.title',
					links: reportLinks,
				},
			],
		};

		if (isAchieveEnabled) {
			admin.sections.splice(3, 0, {
				icon: PlantOutline,
				id: 'one-on-ones-nav',
				title: 'admin_menu.oneonones.title',
				links: oneOnOnesLinks,
			});
		}

		// Determine which section should be expanded based on the current URL
		const determineActiveSectionIndex = (nav: NavConfig): number | undefined => {
			const pathname = currentPathname;

			for (let i = 0; i < nav.sections.length; i++) {
				const section = nav.sections[i];

				for (const link of section.links) {
					// For recognize routes, we need to extract the path from the full URL
					let linkPath = link.url;

					// If the link.url contains a full URL (from useBuildRecognizeURL), extract just the path
					if (link.url.includes('http')) {
						try {
							const url = new URL(link.url);
							linkPath = url.pathname;
						} catch (e) {
							// If it's not a valid URL, use the original value
							linkPath = link.url;
						}
					}

					// Check if the current pathname matches the link URL exactly
					if (linkPath === pathname) {
						return i;
					}

					// Check matchActive regex pattern
					if (link.matchActive && link.matchActive.test(pathname)) {
						return i;
					}

					// Check for partial matches for nested routes
					// For example, /admin/growth/one-on-ones should match if linkPath is /admin/growth
					// But be careful not to match too broadly
					if (linkPath !== '/' && linkPath !== '' && pathname.startsWith(linkPath)) {
						// Make sure it's a proper path segment match
						// Either pathname equals linkPath, or the next character is a '/'
						if (pathname === linkPath || pathname.charAt(linkPath.length) === '/') {
							return i;
						}
					}
				}
			}

			return undefined; // No active section found
		};

		admin.activeSectionIndex = determineActiveSectionIndex(admin);

		const superAdmin: NavConfig = {
			id: 'super-admin-nav',
			label: 'super_admin_menu.menu_label',
			title: 'super_admin_menu.title',
			roles: ['show_superadmin_feature'],
			sections: [
				{
					icon: ChartBarOutline,
					id: 'metrics-nav',
					title: 'super_admin_menu.metrics.title',
					links: [
						{
							label: 'super_admin_menu.metrics.rewards',
							url: r(Route.SuperAdminRewards),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.metrics.rewards_country',
							url: r(Route.SuperAdminRewardsByCountry),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.metrics.bonuses',
							url: r(Route.SuperAdminBonuses),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.metrics.sign_ups',
							url: r(Route.SuperAdminSignUpsMetrics),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.metrics.seats',
							url: r(Route.SuperAdminSeats),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.metrics.integrations',
							url: r(Route.SuperAdminIntegrationsMetrics),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.metrics.participation',
							url: r(Route.SuperAdminParticipation),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.metrics.participation_v2',
							url: r(Route.SuperAdminParticipationV2),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.metrics.bonus_reports',
							url: r(Route.SuperAdminBonusReports),
							isRecognizeRoute: true,
						},
					],
				},
				{
					icon: OfficeBuildingOutline,
					id: 'company-nav',
					title: 'super_admin_menu.companies.title',
					links: [
						{
							label: 'super_admin_menu.companies.manage',
							url: r(Route.SuperAdminManageCompanies),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.companies.sign_ups',
							url: r(Route.SuperAdminCompanySignUps),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.companies.invite_report',
							url: r(Route.SuperAdminInviteReport),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.companies.integrations',
							url: r(Route.SuperAdminCompanyIntegrations),
							isRecognizeRoute: true,
						},
					],
				},
				{
					icon: UserGroupOutline,
					id: 'users-nav',
					title: 'super_admin_menu.users.title',
					links: [
						{
							label: 'super_admin_menu.users.manage',
							url: r(Route.SuperAdminManageUsers),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.users.alias',
							url: r(Route.SuperAdminAliasUsers),
							isRecognizeRoute: true,
						},
					],
				},
				{
					icon: GiftOutline,
					id: 'rewards-nav',
					title: 'super_admin_menu.rewards.title',
					links: [
						{
							label: 'super_admin_menu.rewards.manage',
							url: r(Route.SuperAdminManageRewards),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.rewards.health',
							url: r(Route.SuperAdminRewardsHealth),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.rewards.tango_orders',
							url: r(Route.SuperAdminTangoOrders),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.rewards.manually_fulfilled',
							url: r(Route.SuperAdminManuallyFulfilled),
							isRecognizeRoute: true,
						},
					],
				},
				{
					icon: BookAltOutline,
					id: 'catalog-nav',
					title: 'super_admin_menu.catalog.title',
					links: [
						{
							label: 'super_admin_menu.catalog.manage',
							url: r(Route.SuperAdminManageCatalog),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.catalog.providers',
							url: r(Route.SuperAdminProviders),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.catalog.lint',
							url: r(Route.SuperAdminLint),
							isRecognizeRoute: true,
						},
					],
				},
				{
					icon: PuzzleOutline,
					id: 'developer-nav',
					title: 'super_admin_menu.developer.title',
					links: [
						{
							label: 'super_admin_menu.developer.marmon_sftp',
							url: r(Route.SuperAdminMarmonSFTP),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.developer.videos',
							url: r(Route.SuperAdminVideos),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.developer.internals',
							url: r(Route.SuperAdminInternals),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.developer.email',
							url: r(Route.SuperAdminEmail),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.developer.donation_payouts',
							url: r(Route.SuperAdminDonationPayouts),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.developer.quality_bot_reports',
							url: r(Route.SuperAdminQualityBotReports),
							isRecognizeRoute: true,
						},
					],
				},
				{
					icon: LifeRaftOutline,
					id: 'quality-ops-nav',
					title: 'super_admin_menu.quality_ops.title',
					links: [
						{
							label: 'super_admin_menu.quality_ops.everything',
							roles: ['tech_ops_admin'],
							url: r(Route.TechOps),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.quality_ops.company_specific',
							roles: ['tech_ops_admin'],
							url: r(Route.TechOpsCompany),
							isRecognizeRoute: true,
						},
						{
							label: 'super_admin_menu.quality_ops.user_specific',
							roles: ['tech_ops_admin'],
							url: r(Route.TechOpsUser),
							isRecognizeRoute: true,
						},
					],
				},
			],
		};

		superAdmin.activeSectionIndex = determineActiveSectionIndex(superAdmin);

		return { admin, superAdmin };
	}, [canGiveRecognition, company, isAchieveEnabled, pendingApprovalsCount, r, recognitionBadgeCount, reportedPostsCount, user, currentPathname]);
};
